import {
  LitElement,
  html,
  css,
  unsafeHTML,
  ref,
  createRef,
  paths,
  t,
  styles,
} from '@hh/global.js'

class HHSlider extends LitElement {
  static properties = {
    active: {
      reflect: true,
    },
    breakpoint: {
      type: Number,
    },
    cards: {
      type: Number,
      reflect: true,
    },
    hideDots: {
      type: Boolean,
      reflect: true,
    },
    showScrollbar: {
      type: Boolean,
      reflect: true,
    },
    _current: {
      state: true,
    },
    _total: {
      state: true,
    },
    _lastFocusedSwatch: {
      state: true,
    },
  }

  $container = createRef()
  $next = createRef()
  $prev = createRef()

  constructor() {
    super()

    this._total = 0
    this._current = 1
    this._dots = []
    this._cardsVisible = 0
    this.isScrolling = false

    this.updateUI = this.updateUI.bind(this)
    this.updateDots = this.updateDots.bind(this)
    this.dotEnterKey = this.dotEnterKey.bind(this)

    this.handleResize = this.handleResize.bind(this)
    this.generateDots = this.generateDots.bind(this)
  }

  connectedCallback() {
    super.connectedCallback()

    this.items = Array.from(this.querySelectorAll('[slot="item"]'))
    this._total = this.items.length
    this._cardsVisible = this.isMobile() ? 2 : this.cards

    setTimeout(() => {
      this.$container?.value?.addEventListener(
        'scroll',
        this.handleScroll.bind(this)
      )

      if (this.breakpoint && this.observer) {
        this.observer.observe(document.body)
      }
      this.updateUI()
    }, 0)

    window.addEventListener('resize', this.handleResize)
    window.addEventListener('keydown', this.keyPressListener.bind(this))
  }

  disconnectedCallback() {
    super.disconnectedCallback()

    this.$container?.value?.removeEventListener(
      'scroll',
      this.handleScroll.bind(this)
    )

    this.observer?.disconnect()
    window.removeEventListener('resize', this.handleResize)
    window.removeEventListener('keydown', this.keyPressListener.bind(this))
  }
  handleScroll() {
    this.isScrolling = true

    clearTimeout(this.scrollEndTimer)
    this.scrollEndTimer = setTimeout(() => {
      this.isScrolling = false
      this.updateUI()
    }, 150)
  }

  firstUpdated() {
    this.addEventListener('accordionOpened', this.closeOthers)
  }

  closeOthers(e) {
    const openedAccordion = e.target
    const allAccordions = this.querySelectorAll('hh-accordion')

    allAccordions.forEach((accordion) => {
      if (accordion !== openedAccordion && accordion.open) {
        accordion.open = false
      }
    })
  }

  updated(changedProperties) {
    super.updated(changedProperties)
    if (changedProperties.has('_total') && this._total > 0) {
      this.generateDots()
    }
  }

  navigateSwatches(swatches, direction) {
    let focusedSwatchIndex = swatches.findIndex(
      (swatch) => document.activeElement === swatch
    )
    if (focusedSwatchIndex !== -1) {
      let targetIndex = focusedSwatchIndex + direction
      if (targetIndex >= 0 && targetIndex < swatches.length) {
        swatches[targetIndex].focus()
      }
    }
  }

  keyPressListener(e) {
    if (document.activeElement.classList.contains('card__swatch')) {
      this._lastFocusedSwatch = document.activeElement
      let swatches = Array.from(this.querySelectorAll('.card__swatch'))
      switch (e.code) {
        case 'ArrowRight':
          this.navigateSwatches(swatches, 1)
          break
        case 'ArrowLeft':
          this.navigateSwatches(swatches, -1)
          break
        default:
          break
      }
    }
    let cardsToShow = this.isMobile() ? 2 : this.cards
    const lastVisibleIndex = this._current * cardsToShow - 1
    if (
      document.activeElement === this.items[lastVisibleIndex] &&
      e.code === 'Tab' &&
      !e.shiftKey
    ) {
      e.preventDefault()
      this.$next.value.focus()
    }
  }

  handleResize() {
    this.$container?.value?.scrollTo({ left: 0 })
    this._current = 1
    this.generateDots()
  }

  generateDots() {
    if (this.hideDots || this.cards === 0) {
      return
    }

    let cardsToShow = this.isMobile() ? 2 : this.cards
    let dotCount = Math.ceil(this._total / cardsToShow)
    let dotsContainer = this.shadowRoot.querySelector('.dots')
    if (dotsContainer) {
      dotsContainer.innerHTML = ''
    }

    if (!dotCount) return

    this._dots = Array(dotCount)
      .fill()
      .map((_, i) => {
        let dot = document.createElement('div')
        dot.classList.add('dot')
        dot.setAttribute('role', 'button')
        dot.setAttribute(
          'aria-label',
          `${t('accessibility.slide', 'Slide')} ${i + 1}`
        )
        dot.setAttribute('tabindex', 0)

        if (i === 0) {
          dot.classList.add('active')
        }
        dot.addEventListener('click', () =>
          this.animateContainer(i, cardsToShow)
        )
        dot.addEventListener('keydown', this.dotEnterKey(i, cardsToShow))
        dotsContainer.appendChild(dot)
        return dot
      })
  }

  dotEnterKey(i, cardsToShow) {
    return (e) => {
      if (e.key === 'Enter') {
        this.animateContainer(i, cardsToShow)
      }
    }
  }

  animateContainer(i = 0, cardsToShow) {
    if (!cardsToShow) return
    this.$container?.value?.scrollTo({
      left: i * cardsToShow * this.items[0].clientWidth,
      behavior: 'smooth',
    })
  }

  activate([first, ...all]) {
    const { contentRect: { width = 0 } = {} } = first || {}
    if (width < this.breakpoint && !this.active) {
      this.active = true
    } else if (width >= this.breakpoint && this.active) {
      this.active = false
    }
  }

  updateUI() {
    if (!this._total || this.isScrolling) {
      return
    }

    let cardsToShow = this.isMobile() ? 2 : this.cards

    this._current =
      Math.ceil(
        this.$container?.value?.scrollLeft /
          (cardsToShow * this.items[0].clientWidth + 1)
      ) + 1

    this.updateDots()

    const startIndex = (this._current - 1) * cardsToShow
    const endIndex = this._current * cardsToShow

    this.items.forEach((item, index) => {
      const focusables = Array.from(
        item.querySelectorAll('a, button, [tabindex]')
      )
      const swatches = Array.from(item.querySelectorAll('.card__swatch'))

      if (index >= startIndex && index < endIndex) {
        focusables.forEach((focusable) => {
          if (swatches.includes(focusable)) {
            if (focusable === swatches[0]) {
              focusable.tabIndex = 0
            } else {
              focusable.tabIndex = -1
            }
          } else {
            focusable.tabIndex = 0
          }
        })
      } else {
        focusables.forEach((focusable) => {
          focusable.tabIndex = -1
        })
      }
    })
  }

  updateDots() {
    this._dots.forEach((dot, i) => {
      if (i + 1 === this._current) {
        dot.classList.add('active')
      } else {
        dot.classList.remove('active')
      }
    })
  }

  updateSlider() {
    this.items = Array.from(this.querySelectorAll('[slot="item"]'))
    this._total = this.items.length
    this._cardsVisible = this.isMobile() ? 2 : this.cards
    this.generateDots()
    this.updateUI()
  }

  isMobile() {
    return window.innerWidth < 750
  }

  async move(event) {
    event.preventDefault()
    this.isScrolling = true
    const cardsToMove = this.isMobile() ? 2 : this.cards
    const scrollDistance = cardsToMove * this.items[0].clientWidth
    const slideScrollPosition =
      event.currentTarget.name === 'next'
        ? this.$container?.value?.scrollLeft + scrollDistance
        : this.$container?.value?.scrollLeft - scrollDistance
    this.$container?.value?.scrollTo({
      left: slideScrollPosition,
    })

    await new Promise((resolve) => setTimeout(resolve, 1000))

    const startIndex = (this._current - 1) * cardsToMove
    const firstVisibleCard = this.items[startIndex]
    const firstFocusableInFirstVisibleCard = firstVisibleCard.querySelector(
      'a, button, [tabindex]'
    )

    firstFocusableInFirstVisibleCard?.focus()
  }

  render() {
    const showArrows = this._total > this.cards
    const scrollbarClass = this.showScrollbar ? 'show-scrollbar' : ''

    return html`
      ${showArrows
        ? html`
            <div class="buttons visible--md">
              <button
                ${ref(this.$prev)}
                @click="${this.move}"
                type="button"
                class="button button--prev ${this._current === 1
                  ? '--disabled'
                  : ''}"
                name="previous"
                aria-label="${t('accessibility.previous_slide', 'Previous')}"
                ?disabled="${this._current === 1}"
              >
                ${unsafeHTML(paths.caret)}
              </button>
            </div>
          `
        : ''}
      <div
        class="container ${scrollbarClass}"
        ${ref(this.$container)}
        role="list"
      >
        <slot name="item"></slot>
      </div>
      ${showArrows
        ? html`
            <div class="buttons visible--md">
              <div class="counter visually-hidden">
                <span class="counter--current">${this._current}</span>
                <span>${t('accessibility.of', 'of')}</span>
                <span class="counter--total"
                  >${this._total ? this._total : ''}</span
                >
              </div>
              <button
                ${ref(this.$next)}
                @click="${this.move}"
                type="button"
                class="button button--next ${this._current *
                  this._cardsVisible ===
                this._total
                  ? '--disabled'
                  : ''}"
                name="next"
                aria-label="${t('accessibility.next_slide', 'Next')}"
                ?disabled="${this._current * this._cardsVisible ===
                this._total}"
              >
                ${unsafeHTML(paths.caret)}
              </button>
            </div>
          `
        : ''}
      ${this.hideDots ? '' : html`<div class="dots"></div>`}
    `
  }

  static styles = [
    styles.visuallyHidden,
    css`
      *:focus-visible {
        outline: 0.2rem solid var(--hh-color-focus);
        outline-offset: var(--hh-focus-outline-offset);
      }

      :host([active='true']) {
        position: relative;
      }

      .container {
        width: var(--hh-container-width, 100%);
        display: var(--hh-container-flex);
        flex-wrap: var(--hh-container-flex-wrap);
        margin: var(--hh-container-margin);
        gap: var(--hh-container-gap, 0px);
      }

      :host([active='true']) .container {
        display: flex;
        flex-wrap: var(--hh-active-container-wrap, nowrap);
        position: relative;
        background: var(--hh-background);
        overflow-x: auto;
        scroll-snap-type: x mandatory;
        scroll-behavior: smooth;
        -webkit-overflow-scrolling: touch;
        padding-bottom: 2rem;
        overflow-y: visible;
        overflow-x: auto;
        -ms-overflow-style: none;
        scrollbar-width: none;
      }

      :host([active='true'])
        .container:not(.show-scrollbar)::-webkit-scrollbar {
        display: none;
      }

      .container.show-scrollbar {
        -ms-overflow-style: auto !important;
        scrollbar-width: auto !important;
      }

      .container.show-scrollbar::-webkit-scrollbar {
        display: auto !important;
        width: 5px;
        height: 5px;
      }

      .container.show-scrollbar::-webkit-scrollbar-track {
        background: var(--hh-color-grey-2, #dedae0);
      }

      .container.show-scrollbar::-webkit-scrollbar-thumb {
        background: #bdbdbd;
      }

      :host([active='true']) ::slotted(*) {
        flex-shrink: 0;
        scroll-snap-align: start;
        width: var(--hh-cell-width) !important;
      }

      @media (min-width: 750px) {
        :host([active='true']) .container {
          padding-bottom: 5rem;
          margin-bottom: -4rem;
        }

        :host([active='true']) ::slotted(*) {
          width: var(--hh-cell-width) !important;
        }

        :host([active='true'][cards='6']) ::slotted(*) {
          width: calc(100% / 6) !important;
        }
      }

      .buttons {
        left: 0;
        right: 0;
        top: 1rem;
        z-index: 2;
        display: none;
        position: absolute;
        align-items: center;
        border: var(--hh-button-border);
        background: var(--hh-button-background);
      }

      :host([active='true']) .buttons {
        display: flex;
      }

      .counter {
        margin: 0 2rem;
      }

      .button {
        top: 0;
        z-index: 2;
        width: 80px;
        opacity: 0.5;
        border: none;
        display: none;
        height: 275px;
        cursor: pointer;
        position: absolute;
        color: var(--hh-color);
        transition: opacity var(--hh-anim-default),
          background var(--hh-anim-default);
      }

      :host([active='true'][cards='4']) .button {
        height: 309px;
      }

      @media (min-width: 750px) {
        .button {
          display: block;
        }
      }

      .button:hover {
        opacity: 1;
      }

      .button svg {
        height: 3rem;
      }

      .button--prev {
        left: 0;
        background: linear-gradient(
          90deg,
          #fff 0%,
          rgba(255, 255, 255, 0) 100%
        );
      }

      .button--prev svg {
        transform: rotate(90deg);
      }

      .button--next {
        right: 0;
        background: linear-gradient(
          270deg,
          #fff 0%,
          rgba(255, 255, 255, 0) 100%
        );
      }

      .button--next svg {
        transform: rotate(-90deg);
      }

      .dot {
        cursor: pointer;
        position: relative;
        padding: 1rem 0.5rem;
        display: inline-block;
      }

      .dot:first-of-type {
        margin-left: 1rem;
      }

      .dot:before {
        content: '';
        width: 8px;
        height: 8px;
        display: block;
        position: absolute;
        border-radius: 50%;
        position: relative;
        background-color: var(--hh-color-font);
        transition: background-color var(--hh-anim-default);
      }

      .dot.active:before {
        background-color: var(--hh-color-green);
      }
    `,
  ]
}

customElements.define('hh-slider', HHSlider)
